import { JEV_WAITING_FOR_BUDGET_NOTE, JEV_WAITING_FOR_BUDGET_TITLE, type IcpView, type RankedBy } from '@nest-recruit/shared';

// ADR 0062, mockup M2: the banners above the Ranking list say what Jev is doing and what the order is made of — new CVs being read,
// no key, a job still being moved, a closed job in its old order, nothing readable. Pure, so each wording is unit-tested.

export type NoticeKind = 'waiting' | 'not_connected' | 'moving' | 'old_order' | 'unreadable' | 'budget';

export interface RankingNotice {
  readonly kind: NoticeKind;
  readonly tone: 'info' | 'warn';
  readonly title: string;
  readonly body: string;
  /** A bar and "12 of 40" beside the words. */
  readonly progress: { readonly done: number; readonly total: number } | null;
  /** The button on the banner; "Rank with Jev" on an old order. */
  readonly action: { readonly kind: 'rank'; readonly label: string } | null;
}

export interface NoticeInput {
  readonly rankedBy: RankedBy;
  readonly hasProfile: boolean;
  readonly readsCvs: boolean;
  readonly isJevConnected: boolean;
  readonly rowCount: number;
  readonly needsALookCount: number;
  /** People in no tab for want of Jev's reading. */
  readonly waitingCount: number;
  readonly readingNew: IcpView['readingNew'];
  /** A full ask is reading the whole job now: Rank again, Save and rank, or the move from the criteria. */
  readonly reading: { readonly done: number; readonly total: number } | null;
  readonly autoPausedReason: string | null;
  /** "Ranking 96 CVs with Jev · about 6¢", what Rank with Jev would read and cost. */
  readonly rankEstimate: string;
  /** ADR 0070 stage 2: the job's Jev budget is used up, so reading has stopped for it. */
  readonly isBudgetUsedUp?: boolean;
}

const NOT_CONNECTED: RankingNotice = {
  kind: 'not_connected',
  tone: 'warn',
  title: 'Jev isn’t connected, so CVs aren’t read: ranked on hard facts only.',
  body: 'Dashed boxes are lines Jev would have read. Ask your admin to add the Jev key; people are read on their own after that.',
  progress: null,
  action: null,
};

const UNREADABLE: RankingNotice = {
  kind: 'unreadable',
  tone: 'warn',
  title: 'Jev couldn’t read any CV: they may be scans.',
  body: 'Open the first one and read it yourself, or ask candidates for a text version.',
  progress: null,
  action: null,
};

function plural(count: number): string {
  return count === 1 ? 'CV' : 'CVs';
}

function movingNotice(reading: { readonly done: number; readonly total: number }): RankingNotice {
  return {
    kind: 'moving',
    tone: 'info',
    title: `Moving to the ideal candidate: Jev is reading ${reading.done.toLocaleString('en-US')} of ${reading.total.toLocaleString('en-US')} CVs.`,
    body: 'Until then this list keeps its old order.',
    progress: reading,
    action: null,
  };
}

function oldOrderNotice(rankEstimate: string): RankingNotice {
  return {
    kind: 'old_order',
    tone: 'info',
    title: 'Ranked by the old screening criteria.',
    body: `Rank with Jev to use the ideal candidate · ${rankEstimate}`,
    progress: null,
    action: { kind: 'rank', label: 'Rank with Jev' },
  };
}

/** The banners to show, most important first. A job in its old order only ever says so; a profile job says what Jev is doing. */
export function noticesOf(input: NoticeInput): readonly RankingNotice[] {
  if (input.rankedBy === 'criteria') {
    if (!input.hasProfile) return [];
    return [input.reading ? movingNotice(input.reading) : oldOrderNotice(input.rankEstimate)];
  }
  const notices: RankingNotice[] = [];
  if (!input.isJevConnected && input.readsCvs) notices.push(NOT_CONNECTED);
  if (input.isBudgetUsedUp) notices.push({ kind: 'budget', tone: 'warn', title: JEV_WAITING_FOR_BUDGET_TITLE, body: JEV_WAITING_FOR_BUDGET_NOTE, progress: null, action: null });
  if (input.readingNew) {
    const { total, done, estimateLabel } = input.readingNew;
    notices.push({
      kind: 'waiting',
      tone: 'info',
      title: `Jev is reading ${total.toLocaleString('en-US')} new ${plural(total)} · ${estimateLabel}`,
      body: 'They join the tabs as soon as they are read. Everyone below is already ranked.',
      progress: { done, total },
      action: null,
    });
  } else if (input.waitingCount > 0) {
    notices.push({
      kind: 'waiting',
      tone: 'info',
      title: `${input.waitingCount.toLocaleString('en-US')} new ${plural(input.waitingCount)} waiting for Jev`,
      body: input.autoPausedReason ?? 'They join the tabs as soon as they are read.',
      progress: null,
      action: null,
    });
  }
  const isNothingReadable = input.readsCvs && input.isJevConnected && input.rowCount > 0 && input.needsALookCount === input.rowCount;
  if (isNothingReadable) notices.push(UNREADABLE);
  return notices;
}
